import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useAuthStore } from './useAuthStore';
import {
  getMessages,
  getMessageSignature,
  subscribeToMessages,
  subscribeToTyping,
  sendMessage,
  deleteMessages,
  sendAction as sendActionService,
  getLatestChatId,
  createChatId,
} from '@/services/MessagesService';
import { useWindowStateStore } from './useWindowStateStore';

export const useMessagesStore = defineStore('messages', () => {
  const thread = ref({});
  const assistantTyping = ref(false);
  const messageSignature = ref('AI Assistant');
  const chatId = ref(null);
  let unsubscribeFromThread = null;
  let unsubscribeFromTyping = null;

  const authStore = useAuthStore();
  const { uid } = storeToRefs(authStore);

  const windowStateStore = useWindowStateStore();

  const lastMessage = computed(() => {
    const t = thread.value;
    if (!t || Object.keys(t).length === 0) return null;
    const messageKeys = Object.keys(t);
    const lastKey = messageKeys[messageKeys.length - 1];
    const lastMessage = t?.[lastKey] ?? null;
    if (lastMessage) lastMessage.id = lastKey;
    return lastMessage;
  });

  async function initialize() {
    chatId.value = await getLatestChatId(uid.value);
    thread.value = await getMessages(uid.value, chatId.value);
    messageSignature.value = await getMessageSignature();
    subscribeToThread();
    subscribeToAssistantTyping();
  }

  function subscribeToThread() {
    // Clean up existing message listener
    if (unsubscribeFromThread) {
      unsubscribeFromThread();
      unsubscribeFromThread = null;
    }
    unsubscribeFromThread = subscribeToMessages(
      uid.value,
      chatId.value,
      (msgs) => {
        thread.value = msgs;
      }
    );
  }

  function subscribeToAssistantTyping() {
    // Clean up existing typing listener
    if (unsubscribeFromTyping) {
      unsubscribeFromTyping();
      unsubscribeFromTyping = null;
    }
    unsubscribeFromTyping = subscribeToTyping(
      uid.value,
      (isTyping) => {
        assistantTyping.value = isTyping;
      }
    );
  }

  /**
   * Returns the current chatId, creating a new one if none exists.
   * Re-subscribes the thread listener when a new chatId is created.
   */
  function ensureChatId() {
    if (!chatId.value) {
      chatId.value = createChatId(uid.value);
      subscribeToThread();
    }
    return chatId.value;
  }

  function sendMsg(msg, action) {
    windowStateStore.showContactForm = false;
    const id = ensureChatId();
    sendMessage(uid.value, id, msg, action);
  }

  function sendAction(action, msg) {
    const id = ensureChatId();
    sendActionService(uid.value, id, action, msg);
  }

  function clearChat() {
    deleteMessages(uid.value, chatId.value);
    chatId.value = null;
  }

  function dispose() {
    if (unsubscribeFromThread) {
      unsubscribeFromThread();
      unsubscribeFromThread = null;
    }
    if (unsubscribeFromTyping) {
      unsubscribeFromTyping();
      unsubscribeFromTyping = null;
    }
  }

  return {
    thread,
    chatId,
    lastMessage,
    assistantTyping,
    messageSignature,
    initialize,
    dispose,
    getMessages,
    getMessageSignature,
    sendMsg,
    sendAction,
    ensureChatId,
    clearChat,
  };
});
